import { UseQueryResult } from "@tanstack/react-query";
import { Check, ChevronLeft, ChevronRight, X } from "lucide-react";
import { observer } from "mobx-react-lite";
import {
  MouseEvent,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Button } from "../button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "../dropdown";
import EmptyPlaceholder from "../EmptyPlaceholder";
import { NameWithId } from "../PaginatedSelect/types";

export interface SelectedValue<T> {
  id: string;
  item: T;
}

export interface PaginatedSelectProps<
  T,
  TData extends Record<string, unknown>,
> {
  // Instead of direct data, we use a query result
  query: (page: number) => UseQueryResult<TData, unknown> | null;
  renderItem: (item: T) => ReactNode;
  getItemValue: (item: T) => string;
  placeholder?: string;
  onValueChange?: (value: SelectedValue<T>) => void;
  value?: SelectedValue<NameWithId>;
  className?: string;
  disabled?: boolean;
  onRemove?: () => void;
  // Helper function to extract items and totalPages from the query result
  extractData?: (data: TData) => { data: T[]; totalPages: number };
  // Filter component to be displayed at the top of the dropdown
  filterComponent?: ReactNode;
}

const PaginatedSelect = <T, TData extends Record<string, unknown>>({
  query,
  renderItem,
  getItemValue,
  placeholder = "Select an option",
  onValueChange,
  value,
  className,
  disabled = false,
  onRemove,
  extractData = (data: TData) =>
    data as unknown as { data: T[]; totalPages: number },
  filterComponent,
}: PaginatedSelectProps<T, TData>) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [open, setOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [contentHeight, setContentHeight] = useState<number | null>(null);

  const queryResult = query(currentPage);
  const {
    data: queryData,
    isLoading: isLoadingContent,
    isFetching,
    isError,
  } = queryResult || { data: undefined, isLoading: false, isError: false };

  const isLoading = isLoadingContent || isFetching;
  const { data: items = [], totalPages = 0 } = queryData
    ? extractData(queryData)
    : { data: [] as T[], totalPages: 0 };

  useEffect(() => {
    if (!isLoading && contentRef.current && items.length > 0) {
      const height = contentRef.current.offsetHeight;
      if (height !== contentHeight) {
        setContentHeight(height);
      }
    }
  }, [isLoading, items, contentHeight]);

  const handleItemClick = useCallback(
    (item: T) => {
      if (onValueChange) {
        onValueChange({
          id: getItemValue(item),
          item: item,
        });
      }
      setOpen(false);
    },
    [onValueChange, getItemValue],
  );

  const handleClear = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      if (onRemove) {
        onRemove();
      }
    },
    [onRemove],
  );

  const goToNextPage = useCallback(() => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  }, [currentPage, totalPages]);

  const goToPrevPage = useCallback(() => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  }, [currentPage]);

  // Safe check for item name for display
  const displayText =
    typeof value?.item === "object" &&
    value?.item !== null &&
    "name" in value.item
      ? value.item.name
      : isLoading
      ? "Loading..."
      : placeholder;

  // Check if value exists and has an item with a name property
  const hasNameProperty =
    typeof value?.item === "object" &&
    value?.item !== null &&
    "name" in value.item;

  // Reset to first page when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filterComponent]);

  return (
    <div className="relative w-full">
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger
          ref={triggerRef}
          className={`flex h-9 w-full items-center justify-between rounded-md border border-input bg-white px-3 py-2 text-sm shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
          disabled={disabled || isLoading}
        >
          <div
            className={`flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-start ${
              hasNameProperty ? "text-black" : "text-muted-foreground"
            }`}
          >
            {displayText}
          </div>
          <svg
            width="15"
            height="15"
            viewBox="0 0 15 15"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 opacity-50"
          >
            <path
              d="M4.93179 5.43179C4.75605 5.60753 4.75605 5.89245 4.93179 6.06819C5.10753 6.24392 5.39245 6.24392 5.56819 6.06819L7.49999 4.13638L9.43179 6.06819C9.60753 6.24392 9.89245 6.24392 10.0682 6.06819C10.2439 5.89245 10.2439 5.60753 10.0682 5.43179L7.81819 3.18179C7.73379 3.0974 7.61933 3.04999 7.49999 3.04999C7.38064 3.04999 7.26618 3.0974 7.18179 3.18179L4.93179 5.43179ZM10.0682 9.56819C10.2439 9.39245 10.2439 9.10753 10.0682 8.93179C9.89245 8.75606 9.60753 8.75606 9.43179 8.93179L7.49999 10.8636L5.56819 8.93179C5.39245 8.75606 5.10753 8.75606 4.93179 8.93179C4.75605 9.10753 4.75605 9.39245 4.93179 9.56819L7.18179 11.8182C7.26618 11.9026 7.38064 11.95 7.49999 11.95C7.61933 11.95 7.73379 11.9026 7.81819 11.8182L10.0682 9.56819Z"
              fill="currentColor"
              fillRule="evenodd"
              clipRule="evenodd"
            />
          </svg>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="w-[var(--radix-dropdown-menu-trigger-width)] max-w-none p-0"
          align="start"
        >
          {filterComponent}
          <div
            ref={contentRef}
            style={
              isLoading && contentHeight
                ? { minHeight: `${contentHeight}px` }
                : {}
            }
            className="max-h-[350px] overflow-y-auto p-1"
          >
            {isLoading ? (
              <div className="animate-pulse p-2 text-center text-gray-400">
                <span className="animate-pulse-opacity inline-block">
                  Loading...
                </span>
              </div>
            ) : isError ? (
              <div className="p-2 text-center text-red-500">
                Error loading data
              </div>
            ) : items.length === 0 ? (
              <EmptyPlaceholder message="No results found" className="mb-1" />
            ) : (
              <>
                {items.map((item: T, index: number) => {
                  const itemContent = renderItem(item);
                  const contentAsString =
                    typeof itemContent === "string" ? itemContent : undefined;
                  const isSelected = value?.id === getItemValue(item);

                  return (
                    <div
                      key={`${getItemValue(item)}-${index}`}
                      className={`relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-gray-100 hover:text-black focus:bg-accent focus:text-accent-foreground ${
                        isSelected ? "bg-accent text-accent-foreground" : ""
                      }`}
                      onClick={() => handleItemClick(item)}
                    >
                      <span
                        className="block flex-1 truncate"
                        title={contentAsString}
                      >
                        {itemContent}
                      </span>
                      {isSelected && (
                        <Check className="ml-auto h-4 w-4 flex-shrink-0" />
                      )}
                    </div>
                  );
                })}
              </>
            )}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t px-1 py-2">
              <Button
                variant="outline"
                size="sm"
                onClick={goToPrevPage}
                disabled={currentPage === 1}
                className="h-8 w-8 p-0"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm">
                Page {currentPage} of {totalPages || 1}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={goToNextPage}
                disabled={currentPage === totalPages || totalPages === 0}
                className="h-8 w-8 p-0"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {onRemove && value?.id && (
        <Button
          variant="ghost"
          size="sm"
          onClick={handleClear}
          className="absolute right-7 top-1/2 ml-2 h-4 w-4 -translate-y-1/2 p-0"
          disabled={disabled || isLoading}
        >
          <X className="h-3 w-3" />
        </Button>
      )}
    </div>
  );
};

export default observer(PaginatedSelect);
